import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import type { Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import type { CustomerCategoryToken } from '@sleed/bizcommerce-api';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FormField } from '~/components/system/form-field';
import { applyServerValidation, serverMessage } from '~/lib/api/server-error';
import { useCustomerCategoryMutations } from '~/hooks/use-customer-categories';
import {
	collectDescendantIds,
	customerCategorySchema,
	EMPTY_CUSTOMER_CATEGORY,
	type CustomerCategoryFormValues
} from '~/lib/schemas/customer-category';

const ROOT_VALUE = '0';

interface Props {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	initial: CustomerCategoryFormValues | null;
	all: CustomerCategoryToken[];
}

/** Add / edit a customer category (Κατηγορία Πελάτη). Parent dropdown excludes self + descendants. */
export function CustomerCategoryEditDialog({ open, onOpenChange, initial, all }: Props) {
	const { t } = useTranslation();
	const { create, update } = useCustomerCategoryMutations();
	const form = useForm<CustomerCategoryFormValues>({
		resolver: zodResolver(customerCategorySchema) as Resolver<CustomerCategoryFormValues>,
		defaultValues: initial ?? EMPTY_CUSTOMER_CATEGORY,
		mode: 'onChange'
	});
	const { register, handleSubmit, reset, setError, setValue, watch, formState } = form;
	const isEdit = Boolean(initial?.id);
	const pid = watch('pid');

	useEffect(() => {
		if (open) reset(initial ?? EMPTY_CUSTOMER_CATEGORY);
	}, [open, initial, reset]);

	// Candidate parents: every category except the one being edited and its descendants (avoids cycles).
	const parents = useMemo(() => {
		const excluded = initial?.id ? collectDescendantIds(all, initial.id) : new Set<number>();
		if (initial?.id) excluded.add(initial.id);
		return all.filter(c => c.id != null && !excluded.has(c.id)).sort((a, b) => (a.nm ?? '').localeCompare(b.nm ?? ''));
	}, [all, initial]);

	const onSubmit = handleSubmit(async values => {
		try {
			if (values.id) await update.mutateAsync(values);
			else await create.mutateAsync(values);
			toast.success(t('common.save'));
			onOpenChange(false);
		} catch (err) {
			if (!applyServerValidation(err, setError)) toast.error(serverMessage(err));
		}
	});

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>{isEdit ? t('customers.categories.editTitle') : t('customers.categories.addTitle')}</DialogTitle>
					<DialogDescription>{t('customers.categories.formSubtitle')}</DialogDescription>
				</DialogHeader>
				<form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
					<div className="grid grid-cols-2 gap-3">
						<FormField label={t('customers.categories.columns.name')} error={formState.errors.nm?.message}>
							<Input {...register('nm')} maxLength={100} aria-invalid={Boolean(formState.errors.nm)} autoFocus />
						</FormField>
						<FormField label={t('customers.categories.columns.code')} error={formState.errors.cd?.message}>
							<Input {...register('cd')} maxLength={100} aria-invalid={Boolean(formState.errors.cd)} />
						</FormField>
					</div>
					<FormField label={t('customers.categories.columns.parent')}>
						<Select
							value={pid && pid > 0 ? String(pid) : ROOT_VALUE}
							onValueChange={v => setValue('pid', v === ROOT_VALUE ? null : Number(v), { shouldDirty: true })}>
							<SelectTrigger className="w-full" aria-label={t('customers.categories.columns.parent')}>
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value={ROOT_VALUE}>{t('customers.categories.rootParent')}</SelectItem>
								{parents.map(c => (
									<SelectItem key={c.id} value={String(c.id)}>
										{c.nm}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</FormField>
					<div className="grid grid-cols-2 gap-3">
						<FormField label={t('customers.categories.columns.discount')} error={formState.errors.dsc?.message}>
							<Input
								type="number"
								step="any"
								{...register('dsc', { setValueAs: v => (v === '' || v == null ? null : Number(v)) })}
								aria-invalid={Boolean(formState.errors.dsc)}
							/>
						</FormField>
						<FormField label={t('customers.categories.columns.sort')} error={formState.errors.si?.message}>
							<Input type="number" {...register('si', { valueAsNumber: true })} aria-invalid={Boolean(formState.errors.si)} />
						</FormField>
					</div>
					<div className="flex items-center justify-between rounded-md border border-border px-3 py-2">
						<Label htmlFor="cc-iv">{t('customers.categories.columns.visible')}</Label>
						<Switch id="cc-iv" checked={watch('iv')} onCheckedChange={next => setValue('iv', next, { shouldDirty: true })} />
					</div>
					<DialogFooter>
						<Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
							{t('common.cancel')}
						</Button>
						<Button type="submit" disabled={create.isPending || update.isPending}>
							{create.isPending || update.isPending ? t('common.saving') : t('common.save')}
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
}
